import { useEffect, useRef, useState } from 'react'
import { InlinePayment } from '@/components/audition/inline-payment'
import { useStageWatch } from '@/components/audition/use-stage-watch'
import { Button } from '@/components/ui/button'
import {
  type SubmissionAddress,
  submissionAddressKey,
} from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'
import LoadingFallback from '../loading-fallback'

/**
 * The one pack the application sells, paid inline (LG-1033). Both places
 * an applicant pays — the "Hold your spot" lane and the consideration
 * lane's unlock — sell exactly the catalog's `starter` price, so this
 * component mints the Checkout Session itself and the parent only says
 * what the payment is for.
 *
 * "Paid" is the webhook's word, not the browser's: after the element
 * confirms, this watches the stage until the server agrees.
 *
 * The Session and the "paid" latch may be owned by a parent that outlives
 * this component — `LaneSteps` unmounts the payment panel whenever an
 * earlier step reopens, and a Session minted per mount would burn the
 * `checkout` rate-limit bucket. That ownership is one `commitment` object
 * rather than four loose props so it is all-or-nothing by construction:
 * half of it — a callback with no value to read back, or the reverse —
 * would silently swallow a real charge. `EnrollmentCta` owns one too: its
 * stage gate would otherwise unmount the panel on the refetch that sees
 * the webhook's `client`, taking the "Payment received" line with it.
 * A caller with nothing to remember omits it and this keeps its own.
 */
export interface StarterCommitment {
  clientSecret: string | null
  onClientSecret: (secret: string) => void
  paid: boolean
  onPaid: () => void
}

export function StarterCheckout({
  address,
  submitLabel,
  commitment,
}: {
  address: SubmissionAddress
  submitLabel: string
  commitment?: StarterCommitment
}) {
  const utils = trpc.useUtils()
  const packs = trpc.audition.listPacks.useQuery(address)
  const checkout = trpc.audition.createCheckout.useMutation()
  const [localSecret, setLocalSecret] = useState<string | null>(null)
  const [localPaid, setLocalPaid] = useState(false)
  const clientSecret =
    (commitment ? commitment.clientSecret : localSecret) ??
    checkout.data?.clientSecret ??
    null
  const paid = commitment ? commitment.paid : localPaid
  const watch = useStageWatch({
    address,
    active: paid,
    until: ['paid_pending_call', 'client'],
  })

  const starter = packs.data?.starter ?? null
  const { mutate, isIdle } = checkout
  // Mint as soon as the price is known — there is nothing to pick — but only
  // when there is no Session yet, so reopening this panel reuses the one
  // already minted. Under StrictMode's double effect this mints twice in
  // dev; the abandoned Session simply expires.
  //
  // Keyed on the address's string form, not the object: callers build the
  // literal inline, so identity changes every render while the address does
  // not. `isIdle` is the guard; the key just keeps the effect honest.
  const addressKey = submissionAddressKey(address)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `addressKey` stands in for `address`; see above.
  useEffect(() => {
    if (starter && isIdle && !clientSecret)
      mutate({ ...address, priceId: starter.priceId })
  }, [starter, isIdle, clientSecret, mutate, addressKey])

  // Hand the minted Session up before anything can unmount this panel.
  const minted = checkout.data?.clientSecret
  const onClientSecret = commitment?.onClientSecret
  useEffect(() => {
    if (!minted) return
    if (onClientSecret) onClientSecret(minted)
    else setLocalSecret(minted)
  }, [minted, onClientSecret])

  // The only confirmation of a $2,500 charge, so the live region is mounted
  // from the first render and only its text changes: a `role="status"` that
  // enters the tree together with its content is announced inconsistently
  // across screen readers, and a missed announcement here is a silent charge.
  const status = !paid
    ? ''
    : watch.reached
      ? 'Payment received — your spot is held.'
      : watch.stalled
        ? "Payment received. It's taking a moment to confirm on our side — you'll get an email either way."
        : 'Payment received — confirming…'

  // Confirming drops the whole form, Pay button and all, and focus falls to
  // `<body>` — a keyboard or screen-reader user is dropped at the top of the
  // document right after a $2,500 charge, with no word that it went through.
  // Move focus onto the line that says so. `paid` only ever goes false→true
  // within a mount, so this fires once, as the form leaves.
  const statusRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (paid) statusRef.current?.focus()
  }, [paid])

  let body: React.ReactNode = null
  if (paid) {
    body = null
  } else if (packs.isError) {
    body = (
      <p role="alert" className="text-sm text-destructive">
        We couldn't load enrollment — please refresh and try again.
      </p>
    )
  } else if (packs.isSuccess && !starter) {
    // The catalog carries zero or several starter markers — a dashboard
    // fault the admin catalog page names. Nothing to sell until it is fixed.
    body = (
      <p role="alert" className="text-sm text-destructive">
        Enrollment isn't set up right now — please email us and we'll sort it
        out.
      </p>
    )
  } else if (checkout.isError && !clientSecret) {
    body = (
      <div className="space-y-2">
        <p role="alert" className="text-sm text-destructive">
          We couldn't start checkout — please try again.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => checkout.reset()}
        >
          Try again
        </Button>
      </div>
    )
  } else if (!clientSecret) {
    body = <LoadingFallback />
  } else {
    body = (
      <InlinePayment
        clientSecret={clientSecret}
        submitLabel={submitLabel}
        onPaid={() => {
          if (commitment) commitment.onPaid()
          else setLocalPaid(true)
          void utils.audition.myApplication.invalidate()
        }}
      />
    )
  }

  return (
    <div className="space-y-2">
      <p ref={statusRef} tabIndex={-1} role="status" className="text-sm">
        {status}
      </p>
      {body}
    </div>
  )
}
